import i18n from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import axios from 'axios';
import { ModalProvider } from 'react-modal-hook';
import { useAuth0 } from '@auth0/auth0-react';
import i18nConfig from './i18n';
import { NavigationBar } from './common/components/navigation';
import { Toast } from './common/components/notification';
import { Loader } from './common/components/loaders';
import getEnvUrl from './getEnvUrl';
import UnauthorizedLoginPage from './common/components/errors/UnauthorizedLoginPage';
import Router from './common/components/navigation/Router';
import useStyles from './App.styles';
import { largeCircularLoaderStyle } from './common/components/loaders/Loader.styles';
import ThreatContextProvider from './finding/contexts/findingContext';
import { AbilityContext, ClientContext, userForClaims } from './common/contexts/abilityContext';
import Footer from './common/components/footer/Footer';

const App = () => {
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  const { loginWithRedirect, getAccessTokenSilently, getIdTokenClaims, isAuthenticated, isLoading: isAuth0Loading, error: auth0Error } = useAuth0();

  const initializeApp = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const claims = await getIdTokenClaims();

    axios.defaults.baseURL = getEnvUrl(process.env.REACT_APP_API_BASE_URL);
    axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

    i18n.init(i18nConfig).then(() => {
      axios.defaults.headers.common['Accept-Language'] = i18n.language;
      setUser(userForClaims(claims));
      setIsLoading(false);
    });
  }, [getAccessTokenSilently, getIdTokenClaims]);

  useEffect(() => {
    if (!isAuth0Loading && !auth0Error) {
      if (isAuthenticated) {
        initializeApp();
      } else {
        localStorage.setItem('callbackUrl', window.location.pathname);
        loginWithRedirect();
      }
    }
  }, [auth0Error, isAuth0Loading, isAuthenticated, initializeApp, loginWithRedirect]);

  if (auth0Error) return <UnauthorizedLoginPage />;
  if (isLoading || isAuth0Loading) return <Loader className={classes.loader} circularProgressStyle={largeCircularLoaderStyle} />;
  if (!isAuthenticated) return null;

  return (
    <ClientContext.Provider value={user.client}>
      <AbilityContext.Provider value={user.ability}>
        <ModalProvider>
          <ThreatContextProvider>
            <Toast />
            <BrowserRouter>
              <NavigationBar />
              <Router />
              <Footer />
            </BrowserRouter>
          </ThreatContextProvider>
        </ModalProvider>
      </AbilityContext.Provider>
    </ClientContext.Provider>
  );
};

export default App;
